<template>
  <div class="expense-categories" redesign>
    <!-- Request stepper -->
    <RequestStepProgressBar v-if="organizationDetails.grantRequestAmountEnabled" isExpenseCategories />
    <RequestStepProgressBar v-else isExpenseCategoriesAlt />

    <div class="form-container expense-categories-form">
      <!-- Information -->
      <div class="form-header">
        <span>{{ content }}</span>
      </div>

      <!-- Expense categories -->
      <LoadSpinner v-if="globalLoading" :color="`#${organizationDetails.darkBrandColor}`" />
      <template v-else>
        <div v-for="(expense, category) in expenses" :key="category" class="form-field">
          <ExpenseComponent
            :expense="expense"
            :selected="selectedExpenses.includes(expense.id)"
            :loading="loadingExpenses.includes(category)"
            @expense-selected="onExpenseSelected($event, category)"
            @expense-unselected="onExpenseUnselected($event, category)"
            @amount-updated="calculateTotalAmount()"
            @fieldBlur="onUpdateAmount"
          />
        </div>

        <div v-if="displaySelectExpenseError" class="form-field error-container">
          <div class="tooltip-icon">
            <img src="@/assets/icons/error-icon.png" />
          </div>
          <div class="tooltip">
            <h4 class="tooltip-header" v-translate>No event selected</h4>
            <span class="tooltip-text" v-translate>
              Please select at least one event type and the amount you'd like to request.
            </span>
          </div>
        </div>

        <!-- Total grant amount -->
        <div
          v-if="organizationDetails.grantRequestAmountEnabled"
          class="form-field total-grant-amount"
        >
          <div class="label" v-translate>Total amount requested</div>
          <div class="amount">{{ totalAmount | currency }}</div>
        </div>

        <!-- Next step -->
        <div class="form-footer">
          <div class="next-step">
            <translate>Next step:</translate><br />
            <template v-if="this.currentRequest.requiresExpenseDocs">
              <translate class="next-step-name">Document your expenses</translate>
            </template>
            <template v-else-if="!hasCompletedOnboardingSurveys">
              <translate class="next-step-name">Enrollment Questionnaire</translate>
            </template>
            <template v-else>
              <translate class="next-step-name">Request Summary</translate>
            </template>
          </div>
        </div>

        <!-- Submit -->
        <SubmitButton
          :validate="validate"
          :onContinue="onNext"
          :disable="disabled"
          :loading="loading"
          actionName="commit-expenses"
          :staticWidth="true"
        />
      </template>
    </div>

    <!-- Accelerated modal -->
    <Modal
      :additionalModalStyles="{
        width: '100%',
        'max-width': '500px',
        border: 'none',
        overflow: 'hidden',
      }"
      v-if="
        acceleratedModalOpen &&
        organizationDetails.expenseDocumentationEnabled &&
        organizationDetails.eventDocumentationEnabled
      "
      dimmed
    >
      <div class="form-container card-shape modal-window">
        <div class="form-field">
          <div class="fcolumn">
            <div class="frow" style="padding-bottom: 1rem">
              <p
                v-if="acceleratedEventType == 1"
                v-translate
                :translate-n="numPreselected"
                translate-plural="The information you provided on the last step suggests the simplest path forward is to ask for help with the expense types selected below. Enter the amount you would like to request. Then continue to the next step."
              >
                The information you provided on the last step suggests the simplest path forward is
                to ask for help with the expense type selected below. Enter the amount you would
                like to request. Then continue to the next step.
              </p>
              <p v-else-if="acceleratedEventType == 2 " v-translate>
                The information you provided on the last step suggests the simplest path forward is
                to ask for help with the expense types selected below. Enter the amount you would
                like to request. If you need to add other expense types, select those that apply.
                Then continue to the next step.
              </p>
            </div>
          </div>
        </div>
        <div class="button-container">
          <button @click="acceleratedModalOpen = false" class="form-button">
            <span v-translate>Continue</span>
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import Expense from '@/services/expenses'
import ExpenseCategory from '@/services/expenseCategories'
import { analyticsMixin } from '@/services/googleAnalytics'
import { getDefaultForCustomVariable } from '@/services/organizationCustomVariables'
import Request from '@/services/requests'
import InfoBox from '@/components/InfoBox'
import ExpenseComponent from '@/components/requests/ExpenseComponent'
import LoadSpinner from '@/components/LoadSpinner'
import Modal from '@/components/Modal'
import RequestStepProgressBar from '@/components/stepper/RequestStepProgressBar'
import TextAreaResize from '../../mixins/TextAreaResize'
import SubmitButton from '@/components/requests/SubmitButton'
import UserEvent from '@/services/userEvents'

export default {
  name: 'ExpenseCategories',
  mixins: [analyticsMixin, TextAreaResize],
  components: {
    ExpenseComponent,
    InfoBox,
    LoadSpinner,
    Modal,
    RequestStepProgressBar,
    SubmitButton,
  },
  beforeRouteLeave(to, from, next) {
    let expensesCheck = []
    Object.values(this.expenses).forEach(expense => {
      if (this.selectedExpenses.includes(expense.id)) {
        expensesCheck.push(expense)
      }
    })
    if (
      expensesCheck.some(expense => {
        return !new Set(['Home repair', 'Car repair', 'Medical']).has(expense.categoryRef.name)
      })
    ) {
      this.$store.commit('updateUncommonExpenseCheck', true)
    } else {
      this.$store.commit('updateUncommonExpenseCheck', false)
    }

    next()
  },
  async created() {
    this.globalLoading = true
    // Refresh the request in state
    Request.api.retrieve(this.currentRequest.id).then(request => {
      this.$store.dispatch('updateCurrentRequest', request)
    })

    await this.assembleExpenses()
    // this.startEditingPing()
    ;(this.joinValue = this.$gettext('and')), (this.globalLoading = false)
  },
  mounted() {
    // Update the current request completion status
    UserEvent.api.requestUpdateCompletionStatus(
      this.currentRequest.id,
      UserEvent.options.REQUEST_STEP_EXPENSE_CATEGORIES,
    )
  },
  destroyed() {
    clearInterval(this.interval)
  },
  data() {
    return {
      expenses: [],
      selectedExpenses: [],
      loadingExpenses: [],
      totalAmount: 0,
      globalLoading: false,
      loading: false,
      // true because the computed function that sets this value needs a way to kickoff
      acceleratedModalOpen: true,
      numPreselected: 0,
      pluralPreselectedCopy: this.$gettext(
        'The information you provided on the last step suggests the simplest path forward is to ask for help with the expense types selected below. Enter the amount you would like to request. Then continue to the next step.',
      ),
      joinValue: '',
      displaySelectExpenseError: false,
    }
  },
  methods: {
    getDefaultForCustomVariable,
    /* Assemble an array of Expense objects to populate this screen:
     *    - There will be an Expense for each existing Expense in the database.
     *    - For every other possible ExpenseCategory, there will be an new, empty
     *      Expense.
     */
    async assembleExpenses() {
      //TODO: Update function to start with existing expenses already,
      //then add in the empty ones? (prefer to only add ones that are selected)

      // Pull all Expense Categories
      const res = await ExpenseCategory.api.list()
      const expenseCategories = res.results

      // Create an empty Expense object for each category
      const expenses = {}
      expenseCategories.forEach(category => {
        expenses[category.id] = Expense.create({
          request: this.currentRequest.id,
          category: category.id,
          categoryRef: category,
          amount: 0,
        })
      })

      // For each existing Expense already attached to this request, find the
      // corresponding empty Expense in our list and replace it
      const existingExpenses = this.currentRequest.expensesRef
      const selectedExpenses = []
      existingExpenses.forEach(expense => {
        expenses[expense.category] = { ...expense }
        selectedExpenses.push(expense.id)
      })

      // Set our entire Expense list and selected Expense list
      this.expenses = expenses
      this.selectedExpenses = selectedExpenses
      this.calculateTotalAmount()
    },
    async validate() {
      if (
        this.selectedExpenses.length <= 0 ||
        (this.selectedExpensesWithEmptyAmount() &&
          this.organizationDetails.grantRequestAmountEnabled)
      ) {
        this.displaySelectExpenseError = true
        return Promise.reject('no-expense-selected')
      }
    },
    async onNext() {
      this.loading = true
      Object.values(this.expenses).forEach(expense => {
        if (
          this.organizationDetails.grantRequestAmountEnabled &&
          expense.id &&
          expense.amount <= 0
        ) {
          //TODO: Throw an alert / error here to help the user understand why they can't submit 0 amounts
          //TODO: Do not delete expense, force user to add proper value or deselect expense
          Expense.api.delete(expense)
        }
      })

      const response = await Request.api.retrieve(this.currentRequest.id)
      this.$store.dispatch('updateCurrentRequest', response)

      this.trackClickedCTA()
    },
    /* Add expense to our selected list, then update expense list */
    async onExpenseSelected(expense) {
      this.loadingExpenses.push(expense.category)
      this.calculateTotalAmount()

      if (expense.id) {
        await Expense.api.update({ ...expense })
      } else {
        const response = await Expense.api.create({ ...expense })
        this.expenses[expense.category] = { ...response }
        this.selectedExpenses.push(response.id)
      }

      this.loadingExpenses = this.loadingExpenses.filter(e => e !== expense.category)
    },
    /* Remove expense from our selected list, then update expense list */
    async onExpenseUnselected(expense) {
      this.eventsTracker('deselected-expense-category', `Category ID: ${expense.category}`)
      this.loadingExpenses.push(expense.category)
      this.selectedExpenses = this.selectedExpenses.filter(exp => exp != expense.id)

      if (expense.id) {
        await Expense.api.delete(expense)
        this.expenses[expense.category] = { ...expense, id: null, amount: 0 }
      }
      this.calculateTotalAmount()

      this.loadingExpenses = this.loadingExpenses.filter(e => e !== expense.category)
    },
    async onUpdateAmount() {
      this.calculateTotalAmount()
      this.loading = true
      const updateFields = ['amount']
      const promiseList = Object.values(this.expenses).map(expense => {
        if (this.selectedExpenses.includes(expense.id)) {
          return Expense.api.update({ ...expense }, updateFields)
        }
      })
      await Promise.all(promiseList)
      this.loading = false
    },
    /* Recalculate total amount from all selected expenses */
    calculateTotalAmount() {
      let total = 0
      if (this.organizationDetails.grantRequestAmountEnabled) {
        Object.values(this.expenses).forEach(expense => {
          if (expense.amount && expense.id) {
            total += parseFloat(expense.amount)
          }
        })
      } else {
        if (this.selectedExpenses.length !== 0) {
          total = this.organizationDetails.grantLimit
        }
      }
      this.totalAmount = total
    },
    /* Return true if any expense doesn't have a populated amount */
    selectedExpensesWithEmptyAmount() {
      let emptyExpense = false
      Object.values(this.expenses).forEach(expense => {
        if (expense.id && (!expense.amount || expense.amount == 0)) {
          emptyExpense = true
        }
      })
      return emptyExpense
    },
    startEditingPing() {
      const request = this.currentRequest
      Request.api.editingPing(request)
      this.interval = setInterval(function() {
        Request.api.editingPing(request)
      }, 10000)
    },
    async pickRouteFromExpenseCategories() {
      if (!this.organizationDetails.expenseDocumentationEnabled) {
        return '/request-summary'
      } else {
        return '/expense-documentation'
      }
    },
  },
  computed: {
    ...mapState([
        'user',
        'currentRequest',
        'organizationDetails',
        'requestHasUncommonEventCategories',
        'defaultRequestFlow',
        'reproductiveRightsFlow',
      ]
    ),
    acceleratedEventType() {
      if(!this.currentRequest || !this.currentRequest.eventsRef)
        return 0

      const containsAcceleratedEvent = this.currentRequest.eventsRef.filter((event) => {
        return (
          event.docType == 'invoice' || event.docType == 'receipt' || event.docType == 'estimate'
        )
      })
      const containsEventDocument = this.currentRequest.eventsRef.filter((event) => {
        return event.documentsRef.length > 0
      })
      this.numPreselected = containsAcceleratedEvent.length

      let accelerateEvent = 0

      if (this.numPreselected > 0) {
        this.$store.commit('updateCommonExpenseCheck', true)
      }

      if (this.numPreselected > 0 && this.numPreselected == containsEventDocument.length) {
        accelerateEvent = 1
        this.trackCustomPageview('ExpensePopupPreselect', '/expense-popup-preselect')
      } else if (
        this.numPreselected > 0 &&
        (this.numPreselected < containsEventDocument.length ||
          this.numPreselected < this.currentRequest.eventsRef.length)
      ) {
        accelerateEvent = 2
        this.trackCustomPageview('ExpensePopupPreselect', '/expense-popup-preselect')
      }
      this.acceleratedModalOpen = accelerateEvent > 0
      return accelerateEvent
    },

    disabled() {
      return (
        this.loading ||
        this.globalLoading ||
        this.selectedExpenses.length <= 0 ||
        (this.selectedExpensesWithEmptyAmount() &&
          this.organizationDetails.grantRequestAmountEnabled)
      )
    },
    content() {
      if (this.organizationDetails && this.organizationDetails.expenseDescription) {
          return this.organizationDetails.expenseDescription
      }

      if (this.organizationDetails.grantRequestAmountEnabled) {
        const text = [
          this.$gettext(
            'Select the expenses you need help with due to the emergency or emergencies that prompted this request. Only request what you need right now; remember, we are all part of the same circle. What you don’t need today will be there for you and your',
          ),
          this.$options.filters.translateToCurrentLang(
            this.$language.current,
            '.',
            ' needs tomorrow.',
          ),
        ]

        const defaultCustomVariable = getDefaultForCustomVariable('cv2', this.$language.current)

        const orgCustomVariable =
          this.organizationDetails &&
          (this.organizationDetails.customVariables || this.organizationDetails.customVariables) &&
          (this.organizationDetails.customVariables.cv2 ||
            this.organizationDetails.customVariables.cv2) &&
          this.$options.filters.translateToCurrentLang(
            this.$language.current,
            this.organizationDetails.customVariables.cv2,
            `${this.organizationDetails.customVariables.cv2}'`,
          )

        const apoDefaultCustomVariable =
          this.$language.current === 'en' ? `${defaultCustomVariable}'` : defaultCustomVariable

        return `${text[0]} ${orgCustomVariable || apoDefaultCustomVariable}${text[1]}`
      } else {
        return this.$gettext(
          'How do you plan to use the grant funds? Please select from the list of expense types below. This information is for data analysis purposes only. It does not affect your eligibility for a grant.',
        )
      }
    },
    hasCompletedOnboardingSurveys() {
      if(!this.user)
        return true

      return this.user.hasCompletedOnboardingSurveys
    }
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
@import '@/styles/flex-box';
@import '@/styles/text';
@import '@/styles/mixins/responsive';

.expense-categories {
  display: flex;
  flex-direction: column;

  .expense-categories-form {
    @extend .constrain-tablet-and-up;
  }
}

.modal-link {
  cursor: pointer;
  color: white;
  text-decoration: underline white;

  &:hover {
    text-decoration: underline;
  }
}

.total-grant-amount {
  background-color: var(--dark-brand-color);
  color: white;
  padding: 1.5rem;

  .label {
    font-size: 1.125rem;
  }

  .amount {
    font-size: 2.25rem;
    font-weight: bold;
    line-height: 2.8rem;
  }
}
</style>
